import React, { ReactElement } from 'react';
import { Col, Divider, Empty, Row, Spin, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { ExportOutlined, TeamOutlined } from '@ant-design/icons';
import { UpdateTeamRequest } from '@cbinsights/adminservice/adminservice';
import { useGetSalesforceAccountInfoQuery } from '../../services/hooks/useGetSalesforceAccountInfo';
import UpdateSalesforceAccount from './UpdateSalesforceAccount';

type Props = {
  sfAccountId: string;
  recordedDate: string;
  teamId: number;
  updateTeam: (
    data: Partial<UpdateTeamRequest>,
    teamId: number
  ) => Promise<void>;
  refetchGetTeam: () => void;
};

export const SFDCInfo = ({
  sfAccountId,
  recordedDate,
  teamId,
  updateTeam,
  refetchGetTeam,
}: Props): ReactElement => {
  const { data: salesforceAccountInfo, isLoading: isLoadingAccountInfo } =
    useGetSalesforceAccountInfoQuery({
      enabled: !!sfAccountId,
      params: { accountId: sfAccountId },
    });

  return (
    <Col
      xl={{ push: 2, span: 7 }}
      md={{ push: 2, span: 20 }}
      xs={{ push: 1, span: 22 }}
    >
      <Typography.Title level={4} className="!font-medium mt-4 pl-2">
        Salesforce Information
      </Typography.Title>
      <Divider className="border-t-2 border-black border-solid" />
      <Row justify="space-between">
        <Col span="12">
          <Typography.Title level={5} className="!font-medium pl-2">
            SFDC Account ID :
          </Typography.Title>
        </Col>
        <Col span="12" className="flex justify-end items-center">
          <div className="mr-2">{sfAccountId}</div>
          <UpdateSalesforceAccount
            salesforceAccountId={sfAccountId}
            teamId={teamId}
            updateTeam={updateTeam}
            refetchGetTeam={refetchGetTeam}
          />
        </Col>
      </Row>
      {sfAccountId && (
        <Row justify="space-between">
          <Col span="12">
            <Typography.Title level={5} className="!font-medium pl-2">
              SFDC Account Links :
            </Typography.Title>
          </Col>
          <Tooltip title="See other teams connected to this Salesforce Account">
            <a href={`/teams/sf/${sfAccountId}`} className="text-blue-500">
              {' '}
              <TeamOutlined />
              Other Teams
            </a>
          </Tooltip>
          <a
            href={`https://cbinsights.lightning.force.com/lightning/r/Account/${sfAccountId}/view`}
            target="_blank"
            rel="noreferrer"
            className="text-blue-500"
          >
            Open Salesforce <ExportOutlined />
          </a>
        </Row>
      )}

      {isLoadingAccountInfo && (
        <div className="flex justify-center items-center min-h-[100px]">
          <Spin tip="Loading Salesforce Info..." />
        </div>
      )}
      {!isLoadingAccountInfo && !salesforceAccountInfo && (
        <Empty description="No Salesforce Account Info available." />
      )}
      {!isLoadingAccountInfo && salesforceAccountInfo && (
        <>
          <Row justify="space-between">
            <Col span="12">
              <Typography.Title level={5} className="!font-medium pl-2">
                SFDC Owner :
              </Typography.Title>
            </Col>
            <Col span="12" className="text-right">
              {salesforceAccountInfo.accountOwner}
            </Col>
          </Row>
          <Row justify="space-between">
            <Col span="12">
              <Typography.Title level={5} className="!font-medium pl-2">
                CS Rep Email :
              </Typography.Title>
            </Col>
            <Col span="12" className="text-right">
              {salesforceAccountInfo.accountOwnerEmail}
            </Col>
          </Row>
          <Row justify="space-between">
            <Col span="12">
              <Typography.Title level={5} className="!font-medium pl-2">
                Renewal Date :
              </Typography.Title>
            </Col>
            <Col span="12" className="text-right">
              {salesforceAccountInfo.renewalDate &&
                moment(salesforceAccountInfo.renewalDate).format('MM-DD-YYYY')}
            </Col>
          </Row>
          <Row justify="space-between">
            <Col span="12">
              <Typography.Title level={5} className="!font-medium pl-2">
                Record Created :
              </Typography.Title>
            </Col>
            <Col span="12" className="text-right">
              {recordedDate && moment(recordedDate).format('MM-DD-YYYY')}
            </Col>
          </Row>
        </>
      )}
    </Col>
  );
};
